/**
 * DCC Data Loader - Background loading of full cell data with DCC curves
 * 
 * This utility preloads full cell data (including DCC arrays) in the background
 * to improve UX when users navigate to the My Plot view.
 */

import { getBatchCurves } from '../modules/dataService.js'
import {
  fullCellsCache,
  dccDataLoading,
  dccDataProgress,
  allCells
} from '../state/globalSignals.js'

// Track if loading is already in progress to prevent duplicate loads
let loadingPromise = null

/**
 * Preload full cell data with DCC arrays for all cells
 * Safe to call multiple times - will not restart if already loading
 * 
 * @param {Array} cells - Optional array of cell objects. If not provided, uses allCells signal
 * @returns {Promise<void>} Resolves when loading is complete
 */
export async function preloadDccData(cells = null) {
  // Use provided cells or fall back to global signal
  const cellsToProcess = cells || allCells.value || []
  
  if (cellsToProcess.length === 0) {
    console.log('[DccLoader] No cells to load')
    return
  }
  
  // If already loading, return the existing promise
  if (loadingPromise && dccDataLoading.value) {
    console.log('[DccLoader] DCC data loading already in progress, waiting...')
    return loadingPromise
  }
  
  // Get cell IDs that need full data
  const cellIdsToLoad = cellsToProcess
    .map(cell => cell.id || cell.product_id)
    .filter(id => id && !fullCellsCache.value.has(id))
  
  if (cellIdsToLoad.length === 0) {
    console.log('[DccLoader] All cells already have DCC data cached')
    return
  }
  
  console.log(`[DccLoader] Starting background load of DCC data for ${cellIdsToLoad.length} cells...`)
  dccDataLoading.value = true
  dccDataProgress.value = 0
  
  loadingPromise = (async () => {
    try {
      const startTime = Date.now()

      // Load cells in batches of 10 (batch-curves endpoint max) with progress tracking
      const batchSize = 10
      const newCache = new Map(fullCellsCache.value)

      for (let i = 0; i < cellIdsToLoad.length; i += batchSize) {
        const batch = cellIdsToLoad.slice(i, i + batchSize)

        try {
          const result = await getBatchCurves(batch, ['dcc'])

          // Merge curve data into cache, preserving original explorer data
          // (scalar KPIs, format, chemistry, etc.) so downstream views work
          for (const cellId of batch) {
            const curveData = result.cells?.[cellId]?.dcc || {}
            const meta = result.metadata?.[cellId] || {}
            const existing = newCache.get(cellId) || {}
            // Look up original explorer cell data for scalar fields
            const explorerCell = (allCells.value || []).find(c => c.id === cellId || c.product_id === cellId) || {}
            newCache.set(cellId, {
              ...explorerCell,
              ...existing,
              id: cellId,
              manufacturer: meta.manufacturer || existing.manufacturer || explorerCell.manufacturer,
              name: meta.name || existing.name || explorerCell.name,
              mechanical: {
                ...(explorerCell.mechanical || {}),
                ...(existing.mechanical || {}),
                m_cell: meta.m_cell ?? existing.mechanical?.m_cell ?? explorerCell.mechanical?.m_cell,
                vol_cell: meta.vol_cell ?? existing.mechanical?.vol_cell ?? explorerCell.mechanical?.vol_cell
              },
              electrical: {
                ...(explorerCell.electrical || {}),
                ...(existing.electrical || {}),
                ...curveData
              },
              // Flat access aliases (Step2Performance uses cell.electrical_T_amb_DCC pattern)
              electrical_T_amb_DCC: curveData.T_amb_DCC,
              electrical_P_DCC: curveData.P_DCC,
              electrical_W_DCC: curveData.W_DCC,
              electrical_I_DCC: curveData.I_DCC,
              electrical_C_DCC: curveData.C_DCC,
              electrical_t_max_DCC: curveData.t_max_DCC,
              electrical_eta_DCC: curveData.eta_DCC
            })
          }

          // Update progress
          const progress = Math.round(((i + batch.length) / cellIdsToLoad.length) * 100)
          dccDataProgress.value = progress

        } catch (batchErr) {
          console.warn(`[DccLoader] Batch load error for cells ${i}-${i + batchSize}:`, batchErr)
        }
      }

      // Update cache signal (triggers recompute in components using it)
      fullCellsCache.value = newCache

      const duration = Date.now() - startTime
      console.log(`[DccLoader] ✅ Loaded DCC data for ${newCache.size} cells in ${duration}ms`)

    } catch (err) {
      console.error('[DccLoader] Failed to load DCC data:', err)
    } finally {
      dccDataLoading.value = false
      dccDataProgress.value = 100
      loadingPromise = null
    }
  })()
  
  return loadingPromise
}

/**
 * Check if DCC data is already cached for a cell
 * @param {string} cellId - Cell ID to check
 * @returns {boolean} True if cell has cached DCC data
 */
export function hasDccData(cellId) {
  return fullCellsCache.value.has(cellId)
}

/**
 * Get the current loading progress (0-100)
 * @returns {number} Progress percentage
 */
export function getDccLoadProgress() {
  return dccDataProgress.value
}

/**
 * Check if DCC data is currently loading
 * @returns {boolean} True if loading is in progress
 */
export function isDccLoading() {
  return dccDataLoading.value
}
